import React, { useMemo } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { Campaign } from '../CampaignList';

type CampaignSelectorProps = {
  campaigns: Campaign[];
  selectedCampaignIds: Set<string>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onToggleCampaign: (campaignId: string) => void;
  isChecked: (campaignId: string) => boolean;
};

export const CampaignSelector: React.FC<CampaignSelectorProps> = ({
  campaigns,
  selectedCampaignIds: _selectedCampaignIds,
  searchQuery,
  onSearchChange,
  onToggleCampaign,
  isChecked,
}) => {
  const filteredCampaigns = useMemo(() => {
    if (!searchQuery.trim()) {
      return campaigns;
    }
    const query = searchQuery.toLowerCase();
    return campaigns.filter((campaign) =>
      campaign.name.toLowerCase().includes(query),
    );
  }, [campaigns, searchQuery]);

  return (
    <>
      {/* Search Input */}
      <Box mb={3}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Icon iconName="search" boxSize={4} color="content.muted" />
          </InputLeftElement>
          <Input
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            border="1px solid"
            borderColor="surface.border"
            borderRadius="md"
            pl={10}
            fontFamily="marketingPage"
            _placeholder={{ color: 'content.muted' }}
            _focus={{
              borderColor: 'icon.green',
              boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
            }}
          />
        </InputGroup>
      </Box>

      {/* Selectable Campaigns List */}
      <Box maxH="300px" overflowY="auto" borderRadius="md" p={2}>
        <VStack align="stretch" spacing={2}>
          {filteredCampaigns.map((campaign) => (
            <Box
              key={campaign.id}
              p={3}
              borderRadius="md"
              border="1px solid"
              borderColor="surface.border"
              _hover={{ bg: 'sidebar.itemHoverBg' }}>
              <Checkbox
                isChecked={isChecked(campaign.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleCampaign(campaign.id);
                }}
                label={
                  <Box
                    as="span"
                    fontFamily="marketingPage"
                    fontSize="md"
                    color="content.title"
                    fontWeight="normal">
                    {campaign.name}
                  </Box>
                }
              />
            </Box>
          ))}
        </VStack>
      </Box>
    </>
  );
};
